import React, { useState, useEffect } from 'react';
import { getSupabaseClient } from '../lib/supabase';
import GuideChat from './GuideChat';
import AdminSupportChat from './AdminSupportChat';
import { XMarkIcon, ChatBubbleLeftRightIcon, UserIcon } from '@heroicons/react/24/outline';

type SeekerSummary = {
  seekerId: string;
  authUserId?: string;
  name: string;
  image?: string | null;
  status?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
};

type ChatDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  initialSeekerId?: string | null;
};

const ChatDrawer: React.FC<ChatDrawerProps> = ({ isOpen, onClose, initialSeekerId }) => {
  const [seekers, setSeekers] = useState<SeekerSummary[]>([]);
  const [selectedSeeker, setSelectedSeeker] = useState<SeekerSummary | null>(null);
  const [showAdminChat, setShowAdminChat] = useState(false);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const [adminLastPreview, setAdminLastPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadConnectedSeekers(initialSeekerId || null);
      loadAdminUnreadCount();
    } else {
      // Reset selected seeker when drawer closes
      setSelectedSeeker(null);
      setShowAdminChat(false);
    }
  }, [isOpen, initialSeekerId]);

  const loadAdminUnreadCount = async () => {
    try {
      const supabase = getSupabaseClient();
      const { data } = await supabase
        .from('support_conversations')
        .select('user_unread_count, last_message_preview')
        .maybeSingle();

      if (data) {
        setAdminUnreadCount(data.user_unread_count ?? 0);
        setAdminLastPreview(data.last_message_preview ?? null);
      }
    } catch {
      // No conversation yet, that's fine
    }
  };

  const loadConnectedSeekers = async (autoSelectSeekerId: string | null) => {
    try {
      setLoading(true);
      const supabase = getSupabaseClient();

      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Auth error:', authError);
        return;
      }

      // Fetch all matched seekers
      const { data: matches, error: matchesError } = await supabase
        .from('matches')
        .select(`
          id,
          seeker_id,
          created_at
        `)
        .eq('guide_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (matchesError) {
        console.error('Error fetching matches:', matchesError);
        return;
      }

      // Get unique seeker IDs and build seeker list
      const seekersData: SeekerSummary[] = [];
      const seenSeekerIds = new Set<string>();

      // Collect unique seeker IDs (preserving existing dedup logic)
      const allSeekerIds: string[] = [];
      for (const match of matches || []) {
        if (!match.seeker_id || seenSeekerIds.has(match.seeker_id)) continue;
        seenSeekerIds.add(match.seeker_id);
        allSeekerIds.push(match.seeker_id);
      }

      if (allSeekerIds.length > 0) {
        // Batch 1: seekers — get user_id for all seeker_ids
        const { data: seekerRecords, error: seekerError } = await supabase
          .from('seekers')
          .select('seeker_id, user_id')
          .in('seeker_id', allSeekerIds);

        if (seekerError) {
          console.error('Error fetching seeker records:', seekerError);
        }

        const seekerToUserMap = new Map<string, string>(
          (seekerRecords || [])
            .filter(r => r.seeker_id && r.user_id)
            .map(r => [r.seeker_id as string, r.user_id as string])
        );

        // Collect user IDs for profiles batch
        const allUserIds = [...seekerToUserMap.values()];

        // Batch 2: profiles + Batch 3: package_offers — parallel, independent
        const [profilesResult, offersResult] = await Promise.all([
          allUserIds.length > 0
            ? supabase
                .from('profiles')
                .select('id, full_name')
                .in('id', allUserIds)
            : Promise.resolve({ data: [], error: null }),
          supabase
            .from('package_offers')
            .select('seeker_id, status')
            .eq('guide_id', user.id)
            .in('seeker_id', allSeekerIds)
            .in('status', ['sent', 'accepted']),
        ]);

        if (profilesResult.error) {
          console.error('Error fetching profiles:', profilesResult.error);
        }
        if (offersResult.error) {
          console.error('Error fetching package offers:', offersResult.error);
        }

        const profileMap = new Map<string, string>(
          (profilesResult.data || [])
            .filter(p => p.id && p.full_name)
            .map(p => [p.id as string, (p.full_name as string).trim()])
        );

        // Use most-recent offer per seeker (array order from DB)
        const offerMap = new Map<string, string>();
        for (const offer of offersResult.data || []) {
          if (offer.seeker_id && offer.status && !offerMap.has(offer.seeker_id)) {
            offerMap.set(offer.seeker_id, offer.status as string);
          }
        }

        // Assemble seekersData in the same order as allSeekerIds
        for (const seekerId of allSeekerIds) {
          const userId = seekerToUserMap.get(seekerId);
          if (!userId) {
            console.warn('No seeker record or user_id found for seeker_id:', seekerId);
            continue;
          }

          const fullName = profileMap.get(userId);
          if (!fullName) {
            console.warn('No profile found for user_id:', userId);
            continue;
          }

          const displayName = fullName || 'Seeker';

          const offerStatus = offerMap.get(seekerId);
          let status = 'Connected';
          if (offerStatus) {
            status = offerStatus === 'accepted' ? 'Active Package' : 'Package Sent';
          }

          seekersData.push({
            seekerId,
            authUserId: userId,
            name: displayName,
            status,
            unreadCount: 0 // Will be fetched below
          });
        }
      }

      // Fetch message notification counts for all seekers
      // RLS will automatically filter to only show rows where receiver = current user
      if (seekersData.length > 0) {
        const { data: notifications, error: notifError } = await supabase
          .from('message_notifications')
          .select('sender, count');

        if (!notifError && notifications) {
          // Map notification counts to seekers
          const notificationMap = new Map(
            notifications.map(n => [n.sender, n.count])
          );

          seekersData.forEach(seeker => {
            if (seeker.authUserId) {
              seeker.unreadCount = notificationMap.get(seeker.authUserId) || 0;
            }
          });
        }
      }

      setSeekers(seekersData);

      // Auto-select seeker if we have an initial seeker ID
      if (autoSelectSeekerId) {
        const targetSeeker = seekersData.find(s => s.seekerId === autoSelectSeekerId);
        if (targetSeeker) {
          setSelectedSeeker(targetSeeker);
        }
      }
    } catch (error) {
      console.error('Error loading connected seekers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSeeker = (seeker: SeekerSummary) => {
    setSelectedSeeker(seeker);
  };

  const handleBackToList = () => {
    setSelectedSeeker(null);
    setShowAdminChat(false);
    // Refresh the seeker list to update notification counts
    loadConnectedSeekers(null);
    loadAdminUnreadCount();
  };

  const getInitials = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return 'S';
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'S';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  if (showAdminChat) {
    return (
      <>
        <div
          className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out z-40 hidden sm:block ${
            isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
          }`}
          onClick={handleBackToList}
        />
        <div
          className={`fixed top-0 right-0 h-full w-full sm:w-[480px] bg-[#FFFCEF] shadow-2xl transform transition-transform duration-300 ease-in-out z-50 sm:border-l border-[#2C2731] ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <AdminSupportChat onBack={handleBackToList} />
        </div>
      </>
    );
  }

  if (selectedSeeker) {
    return (
      <>
        {/* Overlay - hidden on mobile since chat is fullscreen */}
        <div
          className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out z-40 hidden sm:block ${
            isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
          }`}
          onClick={handleBackToList}
        />

        {/* Chat panel - fullscreen on mobile, side drawer on larger screens */}
        <div
          className={`fixed top-0 right-0 h-full w-full sm:w-[480px] bg-[#FFFCEF] shadow-2xl transform transition-transform duration-300 ease-in-out z-50 sm:border-l border-[#2C2731] ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <GuideChat seeker={selectedSeeker} onBack={handleBackToList} />
        </div>
      </>
    );
  }

  // Render seekers list
  return (
    <>
      {/* Overlay - hidden on mobile since drawer is fullscreen */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out z-40 hidden sm:block ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer - fullscreen on mobile, side drawer on larger screens */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#FFFCEF] shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full sm:border-l border-[#2C2731]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]">
            <div className="flex items-center gap-3">
              <ChatBubbleLeftRightIcon className="h-6 w-6" />
              <h2 className="text-lg font-semibold">Messages</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full border border-[#FFFCEF]/40 hover:bg-[#FFFCEF]/15 transition-colors text-[#FFFCEF]"
              aria-label="Close chat"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C2731]"></div>
              </div>
            ) : (
              <div className="divide-y divide-[#2C2731]/20">
                {/* Pinned Admin Support entry */}
                <button
                  onClick={() => setShowAdminChat(true)}
                  className={`w-full px-6 py-4 transition-colors text-left flex items-center gap-4 ${
                    adminUnreadCount > 0
                      ? 'bg-[#E8DFBD] hover:bg-[#DED0A8] border-l-4 border-l-[#2C2731]'
                      : 'bg-[#FFFCEF] hover:bg-[#F3EBCD]'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-[#E8DFBD] flex items-center justify-center border border-[#2C2731]/30">
                      <svg className="h-5 w-5 text-[#2C2731]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`text-sm truncate ${adminUnreadCount > 0 ? 'font-bold' : 'font-semibold'} text-[#2C2731]`}>
                        Admin Support
                      </h3>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className={`text-sm truncate ${adminUnreadCount > 0 ? 'text-[#2C2731] font-medium' : 'text-[#2C2731]/70'}`}>
                        {adminLastPreview || 'Message the Guides Collective team'}
                      </p>
                      {adminUnreadCount > 0 && (
                        <span className="ml-2 flex-shrink-0 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-[#FFFCEF] bg-[#2C2731] rounded-full min-w-[20px]">
                          {adminUnreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>

                {seekers.map((seeker) => {
                  const hasUnread = seeker.unreadCount && seeker.unreadCount > 0;
                  return (
                  <button
                    key={seeker.seekerId}
                    onClick={() => handleSelectSeeker(seeker)}
                    className={`w-full px-6 py-4 transition-colors text-left flex items-center gap-4 ${
                      hasUnread
                        ? 'bg-[#E8DFBD] hover:bg-[#DED0A8] border-l-4 border-l-[#2C2731]'
                        : 'bg-[#FFFCEF] hover:bg-[#F3EBCD]'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-[#E8DFBD] flex items-center justify-center border border-[#2C2731]/30">
                        {seeker.image ? (
                          <img
                            src={seeker.image}
                            alt={seeker.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium text-[#2C2731]">
                            {getInitials(seeker.name)}
                          </span>
                        )}
                      </div>
                      {/* Online indicator */}
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-[#FFFCEF]"></div>
                    </div>

                    {/* Seeker info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`text-sm truncate ${hasUnread ? 'font-bold' : 'font-semibold'} text-[#2C2731] ph-mask`}>
                          {seeker.name}
                        </h3>
                        {seeker.lastMessageTime && (
                          <span className={`text-xs ml-2 flex-shrink-0 ${hasUnread ? 'text-[#2C2731] font-semibold' : 'text-[#2C2731]/70'}`}>
                            {seeker.lastMessageTime}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className={`text-sm truncate ${hasUnread ? 'text-[#2C2731] font-medium' : 'text-[#2C2731]/70'} ph-mask`}>
                          {seeker.lastMessage || `Click to start chatting with ${seeker.name.split(' ')[0]}`}
                        </p>
                        {hasUnread && (
                          <span className="ml-2 flex-shrink-0 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-[#FFFCEF] bg-[#2C2731] rounded-full min-w-[20px]">
                            {seeker.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatDrawer;
